import { Head, Link, router } from "@inertiajs/react"
import { useState } from "react"
import { toast } from "sonner"
import { CoachLayout } from "./components/CoachLayout"
import { FunnelStageBadge } from "./components/FunnelStageBadge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { LifeWheelRadar } from "@/components/planning/LifeWheelRadar"
import { ProspectAnswersList } from "@/components/planning/ProspectAnswersList"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import {
  ArrowLeft,
  Check,
  Clock,
  Copy,
  ExternalLink,
  LineChart,
  RefreshCw,
  TriangleAlert,
  Trash2,
  UserPlus,
} from "lucide-react"

// Copy-a-magic-link affordance for the not-completed state — the coach
// pastes it into a personal email. Ported from the pattern in
// MemberShow.jsx; the real "resend invite email" flow is deferred.
function MagicLinkCopyBlock({ inviteLink }) {
  const [copied, setCopied] = useState(false)

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(inviteLink)
    } catch {
      const ta = document.createElement("textarea")
      ta.value = inviteLink
      document.body.appendChild(ta)
      ta.select()
      document.execCommand("copy")
      document.body.removeChild(ta)
    }
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <div>
      <Label className="text-xs uppercase tracking-wide text-zinc-500">
        Magic link — reusable until it expires
      </Label>
      <div className="mt-1 flex gap-2">
        <Input
          value={inviteLink}
          readOnly
          onFocus={(e) => e.target.select()}
          className="font-mono text-xs"
        />
        <Button type="button" variant={copied ? "secondary" : "outline"} size="sm" onClick={copy}>
          {copied ? (
            <>
              <Check className="w-3.5 h-3.5 mr-1" /> Copied
            </>
          ) : (
            <>
              <Copy className="w-3.5 h-3.5 mr-1" /> Copy link
            </>
          )}
        </Button>
      </div>
      <p className="text-xs text-zinc-500 mt-1">
        Paste into a personal email so it reads as coming from you, not an automated blast.
      </p>
    </div>
  )
}

// The Integrated Life Audit wheel. Renders gracefully when the snapshot is
// absent (a submitted/generating prospect may not have one yet — the
// snapshot is best-effort at submit), matching MemberShow's empty-wheel
// behavior.
function WheelCard({ member, snapshot }) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle>
          Integrated Life Audit
          {snapshot?.takenAt && (
            <span className="text-sm text-zinc-500 font-normal ml-2">
              — taken {new Date(snapshot.takenAt).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
                year: "numeric",
              })}
            </span>
          )}
        </CardTitle>
        <Link
          href={`/app/coach/members/${member.id}/wheel_history`}
          className="text-sm text-kf-red hover:underline inline-flex items-center gap-1"
        >
          <LineChart className="w-4 h-4" />
          Wheel history
        </Link>
      </CardHeader>
      <CardContent>
        {snapshot?.wheelTotals ? (
          <LifeWheelRadar scores={snapshot.wheelTotals} size={280} />
        ) : (
          <p className="text-sm text-zinc-500 italic py-8 text-center">
            No wheel snapshot recorded yet.
          </p>
        )}
      </CardContent>
    </Card>
  )
}

function ProspectAnswersCard({ answers }) {
  const hasAny = (answers?.open_ended?.length || 0) + (answers?.yes_no?.length || 0) > 0
  if (!hasAny) return null

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Their assessment answers</CardTitle>
        <p className="text-sm text-zinc-600 font-normal">
          What they shared during the peer-group assessment — context for your
          next conversation.
        </p>
      </CardHeader>
      <CardContent>
        <ProspectAnswersList answers={answers} />
      </CardContent>
    </Card>
  )
}

export default function ProspectShow({
  currentUser,
  member,
  stage,
  stageLabel,
  latestWheelSnapshot = null,
  prospectAnswers = null,
  reportUrl = null,
  reportStatus = null,
  canRegenerateReport = false,
  inviteLink = null,
  backLink,
}) {
  const [removeOpen, setRemoveOpen] = useState(false)
  const [promoteOpen, setPromoteOpen] = useState(false)
  const [promoting, setPromoting] = useState(false)
  const [regenerateOpen, setRegenerateOpen] = useState(false)
  const [regenerating, setRegenerating] = useState(false)

  const back = backLink || { href: "/app/coach/prospects", label: "Back to Prospects", key: "prospects" }
  const backKey = back.key || "prospects"

  // State selection keys on the funnel STAGE (shared with the Prospects
  // index), NOT raw report-row existence — a draft report is still stage
  // "started" = not completed (R6: no wheel/answers until they submit).
  // The shared helper folds submitted/generating/generation_failed into
  // the "submitted" stage; within that engaged branch the report-area
  // sub-state then reads reportStatus to tell pending from failed.
  //   - stage sent / clicked / started -> not-completed empty state
  //   - stage generated (+ url)        -> live "Open assessment report" link
  //   - stage submitted                -> wheel + answers, report unavailable
  //     (pending hint vs. failed indicator)
  const notCompleted = stage === "sent" || stage === "clicked" || stage === "started"
  const canOpenReport = stage === "generated" && !!reportUrl
  const reportFailed = reportStatus === "generation_failed"
  // Only an actually-in-progress report shows the "check back" hint. A
  // report that is "done" server-side but still has no openable link (the
  // rare non-peer_group guest) must NOT read as "generating" forever — it
  // falls through to a neutral "unavailable" card instead.
  const reportPending = reportStatus === "submitted" || reportStatus === "generating"

  const confirmRemove = () => {
    router.delete(`/app/coach/members/${member.id}?back=${encodeURIComponent(backKey)}`)
  }

  const confirmPromote = () => {
    router.post(`/app/coach/members/${member.id}/promote`, {}, {
      onStart: () => setPromoting(true),
      onFinish: () => setPromoting(false),
    })
  }

  // The server decides whether regeneration is allowed (canRegenerateReport,
  // the same predicate its guard reads). After the redirect the report is
  // "submitted", so the existing "Report is generating" card takes over.
  //
  // The redirect lands back on this same URL, and Inertia's `navigate` event
  // (which the global flash toaster listens to) does not fire for a same-URL
  // visit. Toast the flash here in that case only, so it never shows twice.
  const confirmRegenerate = () => {
    const urlBefore = window.location.pathname + window.location.search
    router.post(
      `/app/coach/members/${member.id}/regenerate_report?back=${encodeURIComponent(backKey)}`,
      {},
      {
        onStart: () => setRegenerating(true),
        onSuccess: (page) => {
          if (page.url !== urlBefore) return
          const flash = page.props.flash
          if (flash?.notice) toast.success(flash.notice)
          if (flash?.alert) toast.error(flash.alert)
        },
        onFinish: () => {
          setRegenerating(false)
          setRegenerateOpen(false)
        },
      },
    )
  }

  const regenerateButton = canRegenerateReport && (
    <Button type="button" variant="outline" onClick={() => setRegenerateOpen(true)}>
      <RefreshCw className="w-4 h-4 mr-1.5" />
      Regenerate report
    </Button>
  )

  return (
    <CoachLayout currentUser={currentUser}>
      <Head title={`${member.name} — prospect`} />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        <Link
          href={back.href}
          className="inline-flex items-center gap-1 text-sm text-zinc-600 hover:text-zinc-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          {back.label}
        </Link>

        <div className="flex items-start justify-between mb-2 gap-3 flex-wrap">
          <div>
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="font-display text-3xl">{member.name}</h1>
              <FunnelStageBadge stage={stage} label={stageLabel} />
            </div>
            <p className="text-zinc-500 mt-1">
              Prospect · {member.email}
            </p>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            {!notCompleted && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setPromoteOpen(true)}
              >
                <UserPlus className="w-3.5 h-3.5 mr-1.5" />
                Promote to member
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="text-red-700 border-red-200 hover:bg-red-50 hover:text-red-800"
              onClick={() => setRemoveOpen(true)}
            >
              <Trash2 className="w-3.5 h-3.5 mr-1.5" />
              Remove
            </Button>
          </div>
        </div>

        {notCompleted ? (
          // Not-completed (stage sent/clicked/started): no wheel, no answers,
          // no report link. Show the invite affordance so the coach can
          // re-share their link.
          <div className="mt-6 space-y-6">
            <Card>
              <CardContent className="py-10 text-center">
                <Clock className="w-9 h-9 mx-auto mb-3 text-zinc-300" />
                <p className="font-medium text-zinc-900">Assessment not completed yet</p>
                <p className="text-sm text-zinc-500 mt-1">
                  No Life Wheel or report yet — they haven&rsquo;t finished the assessment.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Assessment invite</CardTitle>
                <p className="text-sm text-zinc-600 font-normal">
                  Copy their sign-in link and resend it personally.
                </p>
              </CardHeader>
              <CardContent>
                {inviteLink ? (
                  <MagicLinkCopyBlock inviteLink={inviteLink} />
                ) : (
                  <p className="text-sm text-zinc-600">
                    No active magic link found (it may have expired). Invite them again from the
                    Prospects list.
                  </p>
                )}
              </CardContent>
            </Card>
          </div>
        ) : (
          // Completed or in-flight: always show the wheel + answers. The
          // report area switches on status.
          <div className="mt-6 space-y-6">
            {canOpenReport ? (
              <Card className="border-emerald-200 bg-emerald-50/40">
                <CardContent className="py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <p className="font-medium text-zinc-900">Assessment report is ready</p>
                    <p className="text-sm text-zinc-600">
                      The same report emailed to you when they finished.
                    </p>
                  </div>
                  <div className="flex flex-wrap items-center gap-2 shrink-0">
                    {regenerateButton}
                    <a href={reportUrl} target="_blank" rel="noreferrer">
                      <Button variant="gradient">
                        <ExternalLink className="w-4 h-4 mr-1.5" />
                        Open assessment report
                      </Button>
                    </a>
                  </div>
                </CardContent>
              </Card>
            ) : reportFailed ? (
              <Card className="border-red-200 bg-red-50/40">
                <CardContent className="py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <TriangleAlert className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
                    <div>
                      <p className="font-medium text-zinc-900">Report couldn&rsquo;t be generated</p>
                      <p className="text-sm text-zinc-600">
                        Their answers are saved below. The report didn&rsquo;t generate. You can try
                        again — it usually takes a few minutes.
                      </p>
                    </div>
                  </div>
                  {regenerateButton && <div className="shrink-0">{regenerateButton}</div>}
                </CardContent>
              </Card>
            ) : reportPending ? (
              <Card className="border-amber-200 bg-amber-50/40">
                <CardContent className="py-5 flex items-start gap-3">
                  <Clock className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-zinc-900">Report is generating</p>
                    <p className="text-sm text-zinc-600">
                      They&rsquo;ve finished the assessment. Check back and refresh in a few minutes
                      — the report link will appear here, and you&rsquo;ll also get it by email.
                    </p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="border-zinc-200 bg-zinc-50">
                <CardContent className="py-5 flex items-start gap-3">
                  <Clock className="w-5 h-5 text-zinc-400 shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-zinc-900">Report isn&rsquo;t available to open here</p>
                    <p className="text-sm text-zinc-600">
                      Their answers are saved below. There&rsquo;s no linkable assessment report for
                      this prospect.
                    </p>
                  </div>
                </CardContent>
              </Card>
            )}

            <WheelCard member={member} snapshot={latestWheelSnapshot} />

            <ProspectAnswersCard answers={prospectAnswers} />
          </div>
        )}
      </div>

      <AlertDialog open={removeOpen} onOpenChange={setRemoveOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this prospect?</AlertDialogTitle>
            <AlertDialogDescription>
              This deactivates {member.name}&rsquo;s account. They will not be able to sign in until
              an admin reactivates them or you invite them again.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmRemove}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Remove prospect
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={promoteOpen} onOpenChange={(open) => { if (!promoting) setPromoteOpen(open) }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Promote {member.name} to member?</AlertDialogTitle>
            <AlertDialogDescription>
              This converts {member.name} from a prospect into an active member and sends them a
              welcome email. Their assessment and answers carry over. You can&rsquo;t undo this from
              here.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={promoting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); confirmPromote() }}
              disabled={promoting}
              className="bg-kf-red hover:bg-kf-red/90 text-white"
            >
              {promoting ? "Promoting…" : "Promote to member"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={regenerateOpen}
        onOpenChange={(open) => { if (!regenerating) setRegenerateOpen(open) }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {reportFailed ? "Try generating this report again?" : "Regenerate this report?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {reportFailed
                ? "It usually takes a few minutes. You'll get an email when it's ready."
                : "This replaces the current report. The prospect's link will show 'being prepared' for a few minutes, then the new version."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={regenerating}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); confirmRegenerate() }}
              disabled={regenerating}
              className="bg-kf-red hover:bg-kf-red/90 text-white"
            >
              {regenerating ? "Starting…" : "Regenerate report"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </CoachLayout>
  )
}
